import { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import FilterBtn from "../../../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../../../Hooks/useApiErrorHandler";
import httpclient from "../../../../../Utils";
import { createFilterHandler } from "../../../../../Utils/FilterUtils";

import { rows } from "./data";
import { columns } from "./columns";
import AuditTrialModal from "./AuditTrialModal";
import Filter from "./Filter";

const dataGridCustomStyles = {
  border: "none",
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "#f5f5f5",
    borderRadius: 1,
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "1px solid #f0f0f0",
    color: "#383E49",
    fontWeight: 500,
  },
  "& .onhold-row": {
    backgroundColor: "#fff8f8",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontFamily: "lexend",
    fontSize: "14px",
    fontWeight: 500,
    color: "#667085",
  },
  "& .MuiDataGrid-row": {
    cursor: "pointer",
  },
};

const INITIAL_FILTER_STATE = {
  jobNo: "",
  custOrder: "",
  orderLineMin: "",
  orderLineMax: "",
  orderedDateStart: null,
  orderedDateEnd: null,
  picker: null,
  orderLoadedStart: null,
  orderLoadedEnd: null,
  orderReachedBayStart: null,
  orderReachedBayEnd: null,
  orderMarkedCompletedStart: null,
  orderMarkedCompletedEnd: null,
  totalDuration: "",
  timePeriod: "",
};

const fieldMappings = {
  jobNo: ["jobNo", "$eq"],
  custOrder: ["custOrder", "$contains"],
  picker: ["picker", "$eq"],
  timePeriod: ["timePeriod", "$eq"],
  totalDuration: ["totalDuration", "$eq"],
};

const rangeFields = {
  orderLine: {
    apiField: "orderLine",
    minField: "orderLineMin",
    maxField: "orderLineMax",
  },
  orderedDate: {
    apiField: "orderedDate",
    minField: "orderedDateStart",
    maxField: "orderedDateEnd",
  },
  orderLoaded: {
    apiField: "orderLoaded",
    minField: "orderLoadedStart",
    maxField: "orderLoadedEnd",
  },
  orderReachedBay: {
    apiField: "orderReachedBay",
    minField: "orderReachedBayStart",
    maxField: "orderReachedBayEnd",
  },
  orderMarkedCompleted: {
    apiField: "orderMarkedCompleted",
    minField: "orderMarkedCompletedStart",
    maxField: "orderMarkedCompletedEnd",
  },
};

export default function OrderEfficiency() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});

  const [loading, setLoading] = useState(false);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchEfficiencyData = async () => {
    const API_URL = "getPlugins.php";

    try {
      const res = await httpclient.get(API_URL);

      const { data } = res;

      if (data.status === 200) {
        setData(data.data);
      } else {
        showNotification("error", data.msg);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // fetchEfficiencyData();
  }, []);

  const handleRowClick = (params) => {
    setSelectedJob(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChangeFilter = (field) => (eventOrValue) => {
    // Handle date pickers (eventOrValue is the Day.js object)
    if (
      field.includes("Date") ||
      field.includes("Start") ||
      field.includes("End")
    ) {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || null, // Store Day.js object or null
      }));
      return;
    }

    // Handle Autocomplete (eventOrValue is the selected value)
    if (field === "picker") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue || "",
      }));
      return;
    }

    // Handle Select dropdown (eventOrValue is the event)
    if (field === "timePeriod") {
      setFilterData((prev) => ({
        ...prev,
        [field]: eventOrValue.target.value,
      }));
      return;
    }

    // Handle regular text/number inputs (eventOrValue is the event)
    setFilterData((prev) => ({
      ...prev,
      [field]: eventOrValue.target.value,
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: setData,
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: "getPlugins.php",
    fieldMappings,
    rangeFields,
  });

  const onFilterClick = () => {
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: [
        "picker",
        "orderedDateStart",
        "orderedDateEnd",
        "orderLoadedStart",
        "orderLoadedEnd",
        "orderReachedBayStart",
        "orderReachedBayEnd",
        "orderMarkedCompletedStart",
        "orderMarkedCompletedEnd",
      ].includes(field)
        ? null
        : "",
    }));
    setSubmittedData((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // Text filters (jobNo, custOrder, totalDuration)
      if (submittedData.jobNo && !row.jobNo.includes(submittedData.jobNo)) {
        return false;
      }
      if (
        submittedData.custOrder &&
        !row.custOrder.includes(submittedData.custOrder)
      ) {
        return false;
      }
      if (
        submittedData.totalDuration &&
        !row.totalDuration.includes(submittedData.totalDuration)
      ) {
        return false;
      }

      // Numeric range filters (orderLine)
      const orderLine = Number(row.orderLine);
      if (
        submittedData.orderLineMin &&
        orderLine < Number(submittedData.orderLineMin)
      ) {
        return false;
      }
      if (
        submittedData.orderLineMax &&
        orderLine > Number(submittedData.orderLineMax)
      ) {
        return false;
      }

      // Picker filter
      if (submittedData.picker && row.picker !== submittedData.picker) {
        return false;
      }

      // Time period filter
      if (submittedData.timePeriod) {
        const now = dayjs();
        let periodStart;

        switch (submittedData.timePeriod) {
          case "Today":
            periodStart = now.startOf("day");
            break;
          case "7 Days":
            periodStart = now.subtract(7, "day").startOf("day");
            break;
          case "14 Days":
            periodStart = now.subtract(14, "day").startOf("day");
            break;
          case "1 Month":
            periodStart = now.subtract(1, "month").startOf("day");
            break;
          case "1 Year":
            periodStart = now.subtract(1, "year").startOf("day");
            break;
          default:
            break;
        }

        const rowDate = dayjs(row.orderedDate);
        if (rowDate.isBefore(periodStart)) {
          return false;
        }
      }

      const rowOrderedDate = new Date(row.orderedDate);
      if (
        (submittedData.orderedDateStart &&
          rowOrderedDate <
            submittedData.orderedDateStart.startOf("day").toDate()) ||
        (submittedData.orderedDateEnd &&
          rowOrderedDate > submittedData.orderedDateEnd.endOf("day").toDate())
      ) {
        return false;
      }

      const rowOrderLoaded = new Date(row.orderLoaded);
      if (
        (submittedData.orderLoadedStart &&
          rowOrderLoaded <
            submittedData.orderLoadedStart.startOf("day").toDate()) ||
        (submittedData.orderLoadedEnd &&
          rowOrderLoaded > submittedData.orderLoadedEnd.startOf("day").toDate())
      ) {
        return false;
      }

      const rowOrderReachedBay = new Date(row.orderReachedBay);
      if (
        (submittedData.orderReachedBayStart &&
          rowOrderReachedBay <
            submittedData.orderReachedBayStart.startOf("day").toDate()) ||
        (submittedData.orderReachedBayEnd &&
          rowOrderReachedBay >
            submittedData.orderReachedBayEnd.startOf("day").toDate())
      ) {
        return false;
      }

      const rowOrderMarkedCompleted = new Date(row.orderMarkedCompleted);
      if (
        (submittedData.orderMarkedCompletedStart &&
          rowOrderMarkedCompleted <
            submittedData.orderMarkedCompletedStart.startOf("day").toDate()) ||
        (submittedData.orderMarkedCompletedEnd &&
          rowOrderMarkedCompleted >
            submittedData.orderMarkedCompletedEnd.startOf("day").toDate())
      ) {
        return false;
      }

      return true;
    });
  }, [submittedData]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "lexend",
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#5D6679",
                }}
              >
                Efficiency Pickup Report by Order
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Grid>
            <Filter
              filterOpen={filterOpen}
              filterData={filterData}
              handleChangeFilter={handleChangeFilter}
              handleFilter={onFilterClick}
              handleClearFilters={handleClearFilters}
              handleRemove={handleRemove}
              submittedData={submittedData}
            />
          </Grid>

          <DataGrid
            rows={filteredRows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            getRowClassName={(params) => {
              return params.row.status === "On Hold" ? "onhold-row" : "";
            }}
            sx={dataGridCustomStyles}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
          />

          <AuditTrialModal
            open={open}
            handleClose={handleClose}
            selectedJob={selectedJob}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
